import React, { useEffect, useMemo, useState } from "react";
import productService from "@/services/productService";
import categoryService from "@/services/categoryService";
import ingredientService from "@/services/ingredientService";
import { getApiErrorMessage, getApiErrorCode } from "@/services/apiClient";
import { showError, showInfo, showWarning } from "@/store/errorStore";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Package,
  Plus,
  RotateCw,
  Search,
  SlidersHorizontal,
  X,
  Zap,
} from "lucide-react";
import { cn } from "@/lib/utils";
import ProductFormModal from "@/components/product/ProductFormModal";
import ProductListItem, { getStockStatus } from "./ProductListItem";

// Filtros rápidos (chips): todos, uma categoria (`cat:<id>`), pausados ou estoque baixo
const ALL = "all";
const PAUSED = "paused";
const LOW_STOCK = "low";

const SORTS = {
  recent: { label: "Mais recentes" },
  name: {
    label: "Nome (A–Z)",
    compare: (a, b) => String(a.name).localeCompare(String(b.name), "pt-BR"),
  },
  price_asc: {
    label: "Menor preço",
    compare: (a, b) => Number(a.price || 0) - Number(b.price || 0),
  },
  price_desc: {
    label: "Maior preço",
    compare: (a, b) => Number(b.price || 0) - Number(a.price || 0),
  },
  stock_asc: {
    label: "Menor estoque",
    compare: (a, b) => Number(a.quantity || 0) - Number(b.quantity || 0),
  },
};

/** Minúsculas e sem acento, para a busca */
const normalize = (s) =>
  String(s ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

const isPaused = (p) => p.available === false;
// "Estoque baixo" junta o que está no mínimo e o que já esgotou (repor)
const isLowStock = (p) => getStockStatus(p).level !== "ok";

function FilterChip({ active, tone = "default", onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-3 py-1 text-xs transition-colors",
        active
          ? "border-primary bg-primary font-semibold text-primary-foreground shadow-sm"
          : tone === "danger"
          ? "border-destructive/20 bg-destructive/10 font-semibold text-destructive hover:bg-destructive/15"
          : tone === "warning"
          ? "border-terra/25 bg-terra-soft font-semibold text-terra hover:bg-terra-soft/70"
          : "border-border bg-card text-muted-foreground hover:bg-surface"
      )}
    >
      {tone !== "default" && !active && (
        <span
          className={cn(
            "h-1.5 w-1.5 rounded-full",
            tone === "danger" ? "bg-destructive" : "bg-terra"
          )}
        />
      )}
      {children}
    </button>
  );
}

function ListSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-2 lg:grid-cols-2" aria-hidden="true">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="flex items-center gap-3 rounded-xl border bg-card p-2.5"
        >
          <Skeleton className="h-16 w-16 rounded-lg md:h-20 md:w-20" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-4 w-24" />
          </div>
          <Skeleton className="h-9 w-20 rounded-lg" />
        </div>
      ))}
    </div>
  );
}

export default function ProductsManagement() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  // Edição: aguardando o GET /products/{id} com o produto completo
  const [loadingProduct, setLoadingProduct] = useState(false);

  // Pausar/ativar em andamento, por id do produto
  const [togglingIds, setTogglingIds] = useState({});

  // Exclusão: produto a confirmar e produto que não pôde ser removido
  const [productToDelete, setProductToDelete] = useState(null);
  const [blockedProduct, setBlockedProduct] = useState(null);
  const [blockedMessage, setBlockedMessage] = useState("");

  // Busca, filtro rápido e ordenação
  const [searchQuery, setSearchQuery] = useState("");
  const [quickFilter, setQuickFilter] = useState(ALL);
  const [sortBy, setSortBy] = useState("recent");

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchIngredients();
  }, []);

  const fetchProducts = async () => {
    setFetching(true);
    try {
      const data = await productService.getAdminAll();
      setProducts(Array.isArray(data) ? data : []);
      setLoadError(false);
    } catch (error) {
      // Modal global já exibido pelo interceptor
      setLoadError(true);
    } finally {
      setFetching(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const data = await categoryService.getAll();
      setCategories(Array.isArray(data) ? data : []);
    } catch (error) {
      // Modal global já exibido pelo interceptor
    }
  };

  const fetchIngredients = async () => {
    try {
      const data = await ingredientService.getAll();
      setIngredients(Array.isArray(data) ? data : []);
    } catch (error) {
      // Modal global já exibido pelo interceptor
    }
  };

  const categoryNameById = useMemo(
    () => new Map(categories.map((c) => [String(c.id), c.name])),
    [categories]
  );

  // Contagens dos chips (sobre a lista inteira, para não "pularem" na busca)
  const counts = useMemo(() => {
    const byCategory = new Map();
    let paused = 0;
    let low = 0;
    products.forEach((p) => {
      const key = String(p.category ?? "");
      byCategory.set(key, (byCategory.get(key) || 0) + 1);
      if (isPaused(p)) paused += 1;
      if (isLowStock(p)) low += 1;
    });
    return { byCategory, paused, low };
  }, [products]);

  // Categorias com produtos (a selecionada continua visível mesmo se zerar)
  const categoryChips = useMemo(
    () =>
      categories.filter(
        (c) =>
          counts.byCategory.get(String(c.id)) > 0 ||
          quickFilter === `cat:${c.id}`
      ),
    [categories, counts, quickFilter]
  );

  const visibleProducts = useMemo(() => {
    const term = normalize(searchQuery).trim();
    const list = products.filter((p) => {
      if (
        term &&
        !normalize(p.name).includes(term) &&
        !normalize(p.description).includes(term)
      ) {
        return false;
      }
      if (quickFilter === PAUSED) return isPaused(p);
      if (quickFilter === LOW_STOCK) return isLowStock(p);
      if (quickFilter.startsWith("cat:")) {
        return String(p.category) === quickFilter.slice(4);
      }
      return true;
    });
    const compare = SORTS[sortBy]?.compare;
    return compare ? [...list].sort(compare) : list;
  }, [products, searchQuery, quickFilter, sortBy]);

  const hasFilters = Boolean(searchQuery.trim()) || quickFilter !== ALL;

  const clearFilters = () => {
    setSearchQuery("");
    setQuickFilter(ALL);
  };

  const openCreate = () => {
    setEditingProduct(null);
    setLoadingProduct(false);
    setShowProductModal(true);
  };

  const handleSaveProduct = async (payload, editing) => {
    try {
      if (editing) {
        await productService.update(editing.id, payload);
      } else {
        await productService.create(payload);
      }
      await fetchProducts();
      setShowProductModal(false);
      showInfo(
        editing ? "Produto atualizado com sucesso" : "Produto criado com sucesso",
        "Produto"
      );
      return true;
    } catch (error) {
      // Modal global já exibido pelo interceptor; mantém o formulário aberto
      return false;
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setShowProductModal(true);
    // GET /products/{id} traz o produto completo (extraIngredients/extraProducts)
    setLoadingProduct(true);
    productService
      .getById(product.id)
      .then((full) => {
        if (full) {
          setEditingProduct((current) =>
            current?.id === product.id ? full : current
          );
        }
      })
      .catch(() => {
        // Modal global já exibido pelo interceptor; segue com o item da lista
      })
      .finally(() => setLoadingProduct(false));
  };

  /** Atualiza só o `available` do item na lista, sem recarregar tudo */
  const applyAvailability = (id, available) =>
    setProducts((prev) =>
      prev.map((p) => (p.id === id ? { ...p, available } : p))
    );

  const handleToggleAvailability = async (product) => {
    if (togglingIds[product.id]) return;
    const available = isPaused(product);
    setTogglingIds((prev) => ({ ...prev, [product.id]: true }));
    try {
      const saved = await productService.setAvailability(product.id, available);
      applyAvailability(
        product.id,
        typeof saved?.available === "boolean" ? saved.available : available
      );
      if (available && getStockStatus(product).level === "out") {
        showWarning(
          `"${product.name}" foi ativado, mas está sem estoque. Registre uma entrada na tela de Estoque para que ele possa ser vendido.`,
          "Produto sem estoque"
        );
      }
    } catch (error) {
      // Modal global já exibido pelo interceptor
    } finally {
      setTogglingIds(({ [product.id]: _, ...rest }) => rest);
    }
  };

  const handleConfirmDelete = async () => {
    const product = productToDelete;
    setProductToDelete(null);
    if (!product) return;
    try {
      await productService.delete(product.id);
      showInfo("Produto excluído com sucesso", "Produto");
      await fetchProducts();
    } catch (error) {
      if (getApiErrorCode(error) === "ERRC095") {
        // Produto usado como adicional de outros produtos
        const parents = /produto\(s\):\s*(.+?)\.\s*Remova/i.exec(
          getApiErrorMessage(error) || ""
        )?.[1];
        showWarning(
          `"${product.name}" é adicional ${
            parents ? `de: ${parents}` : "de outro(s) produto(s)"
          }. Edite ${
            parents ? "esses produtos" : "o(s) produto(s)"
          } e remova-o dos "Produtos adicionais" antes de excluir.`,
          "Produto usado como adicional"
        );
      } else if (error.response?.status === 400) {
        // Pedidos, estoque ou combo vinculados: oferecer pausar
        setBlockedMessage(
          getApiErrorMessage(error) ||
            "O produto possui pedidos, estoque ou combos vinculados."
        );
        setBlockedProduct(product);
      } else {
        showError(
          getApiErrorMessage(error) || "Não foi possível excluir o produto.",
          "Erro ao excluir"
        );
      }
    }
  };

  const handleMarkUnavailable = async () => {
    const product = blockedProduct;
    setBlockedProduct(null);
    if (!product) return;
    try {
      await productService.setAvailability(product.id, false);
      applyAvailability(product.id, false);
      showInfo(`"${product.name}" foi pausado no cardápio`, "Produto");
    } catch (error) {
      // Modal global já exibido pelo interceptor
    }
  };

  const total = products.length;

  return (
    <div className="mx-auto w-full max-w-6xl space-y-4">
      {/* Título com contador e botão Novo */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-brand-pistachio/20 text-primary">
            <Package className="h-5 w-5" />
          </div>
          <div className="flex min-w-0 items-baseline gap-2">
            <h1 className="font-display text-xl font-bold tracking-tight text-foreground md:text-2xl">
              Produtos
            </h1>
            {!fetching || total > 0 ? (
              <span className="whitespace-nowrap rounded-full border border-border bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                {total} {total === 1 ? "item" : "itens"}
              </span>
            ) : null}
          </div>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="flex h-9 flex-shrink-0 items-center gap-1 rounded-lg bg-primary px-3 text-sm font-semibold text-primary-foreground shadow-sm transition-all hover:bg-brand-dark active:scale-95"
        >
          <Plus className="h-4 w-4" />
          <span>Novo</span>
          <span className="hidden sm:inline">produto</span>
        </button>
      </div>

      {/* Busca + ordenação */}
      <div className="flex items-center gap-2">
        <div className="relative flex flex-1 items-center">
          <Search className="pointer-events-none absolute left-3 h-4 w-4 text-muted-foreground" />
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Buscar produto ou sabor..."
            aria-label="Buscar produto"
            className="h-10 w-full rounded-lg border border-border bg-surface pl-9 pr-3 text-sm text-foreground transition-colors placeholder:text-muted-foreground/80 focus:border-primary focus:bg-card focus:outline-none focus:ring-[3px] focus:ring-brand-pistachio/25"
          />
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label="Ordenar produtos"
              title="Ordenar"
              className="relative flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border border-border bg-surface text-muted-foreground transition-colors hover:bg-muted hover:text-primary"
            >
              <SlidersHorizontal className="h-4 w-4" />
              {sortBy !== "recent" && (
                <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-primary" />
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuLabel>Ordenar por</DropdownMenuLabel>
            <DropdownMenuRadioGroup value={sortBy} onValueChange={setSortBy}>
              {Object.entries(SORTS).map(([key, { label }]) => (
                <DropdownMenuRadioItem key={key} value={key}>
                  {label}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Chips de filtro rápido (scroll horizontal no celular) */}
      <div
        className="no-scrollbar -mx-3.5 flex items-center gap-1.5 overflow-x-auto px-3.5 pb-0.5 md:mx-0 md:flex-wrap md:px-0"
        role="group"
        aria-label="Filtrar produtos"
      >
        <FilterChip
          active={quickFilter === ALL}
          onClick={() => setQuickFilter(ALL)}
        >
          Todos ({total})
        </FilterChip>
        {categoryChips.map((c) => (
          <FilterChip
            key={c.id}
            active={quickFilter === `cat:${c.id}`}
            onClick={() => setQuickFilter(`cat:${c.id}`)}
          >
            {c.name} ({counts.byCategory.get(String(c.id)) || 0})
          </FilterChip>
        ))}
        {(counts.paused > 0 || quickFilter === PAUSED) && (
          <FilterChip
            tone="danger"
            active={quickFilter === PAUSED}
            onClick={() => setQuickFilter(PAUSED)}
          >
            Pausados ({counts.paused})
          </FilterChip>
        )}
        {(counts.low > 0 || quickFilter === LOW_STOCK) && (
          <FilterChip
            tone="warning"
            active={quickFilter === LOW_STOCK}
            onClick={() => setQuickFilter(LOW_STOCK)}
          >
            Estoque baixo ({counts.low})
          </FilterChip>
        )}
      </div>

      {/* Regra operacional do cardápio (automática no backend) */}
      <div className="flex items-center gap-1.5 rounded-lg border border-brand-pistachio/40 bg-secondary/60 px-3 py-2">
        <Zap className="h-4 w-4 flex-shrink-0 text-primary" />
        <p className="text-[11px] font-medium text-primary md:text-xs">
          Produtos esgotados saem do cardápio automaticamente e voltam quando o
          estoque é reposto.
        </p>
      </div>

      {/* Lista */}
      {fetching && total === 0 ? (
        <ListSkeleton />
      ) : loadError && total === 0 ? (
        <div className="flex flex-col items-center rounded-xl border border-dashed bg-card/60 px-4 py-12 text-center">
          <Package className="mb-3 h-12 w-12 text-muted-foreground/40" />
          <p className="font-display text-base font-semibold text-foreground">
            Não foi possível carregar os produtos
          </p>
          <button
            type="button"
            onClick={fetchProducts}
            className="mt-4 flex h-9 items-center gap-1 rounded-lg border border-border bg-surface px-3 text-sm font-semibold text-primary transition-colors hover:bg-muted"
          >
            <RotateCw className="h-4 w-4" />
            Tentar novamente
          </button>
        </div>
      ) : visibleProducts.length === 0 ? (
        <div className="flex flex-col items-center rounded-xl border border-dashed bg-card/60 px-4 py-12 text-center">
          <Package className="mb-3 h-12 w-12 text-muted-foreground/40" />
          <p className="font-display text-base font-semibold text-foreground">
            {hasFilters ? "Nenhum produto encontrado" : "Nenhum produto cadastrado"}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {hasFilters
              ? "Tente outra busca ou limpe os filtros."
              : "Cadastre o primeiro produto do cardápio."}
          </p>
          <button
            type="button"
            onClick={hasFilters ? clearFilters : openCreate}
            className={cn(
              "mt-4 flex h-9 items-center gap-1 rounded-lg px-3 text-sm font-semibold transition-colors",
              hasFilters
                ? "border border-border bg-surface text-primary hover:bg-muted"
                : "bg-primary text-primary-foreground hover:bg-brand-dark"
            )}
          >
            {hasFilters ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
            {hasFilters ? "Limpar filtros" : "Novo produto"}
          </button>
        </div>
      ) : (
        <>
          {hasFilters && (
            <div className="flex items-center justify-between px-1 text-xs text-muted-foreground">
              <span>
                Mostrando {visibleProducts.length} de {total}
              </span>
              <button
                type="button"
                onClick={clearFilters}
                className="font-semibold text-primary hover:underline"
              >
                Limpar filtros
              </button>
            </div>
          )}
          <div
            className={cn(
              "grid grid-cols-1 gap-2 transition-opacity lg:grid-cols-2",
              fetching && "opacity-60"
            )}
          >
            {visibleProducts.map((product) => (
              <ProductListItem
                key={product.id}
                product={product}
                categoryName={categoryNameById.get(String(product.category))}
                toggling={!!togglingIds[product.id]}
                onToggle={handleToggleAvailability}
                onEdit={handleEdit}
                onDelete={setProductToDelete}
              />
            ))}
          </div>
        </>
      )}

      {/* Modal de Produto */}
      <ProductFormModal
        open={showProductModal}
        onOpenChange={setShowProductModal}
        editingProduct={editingProduct}
        onSave={handleSaveProduct}
        categories={categories}
        ingredientsCatalog={ingredients}
        productsCatalog={products}
        loadingProduct={loadingProduct}
      />

      {/* Confirmação de exclusão */}
      <AlertDialog
        open={!!productToDelete}
        onOpenChange={(open) => !open && setProductToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir produto</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir "{productToDelete?.name}"? Esta
              ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Produto com vínculos: oferecer pausar no cardápio */}
      <AlertDialog
        open={!!blockedProduct}
        onOpenChange={(open) => !open && setBlockedProduct(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Não foi possível excluir</AlertDialogTitle>
            <AlertDialogDescription>
              {blockedMessage} Você pode pausar "{blockedProduct?.name}" para
              tirá-lo do cardápio.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Fechar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleMarkUnavailable}
              className="bg-primary hover:bg-brand-dark"
            >
              Pausar no cardápio
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
